import { useMemo, useState } from 'react';
import { Button } from '@/components/Button/Button';
import { Modal } from '@/components/Modal/Modal';
import { TextField } from '@/components/TextField/TextField';
import { t } from '@/lib/i18n';
import type {
  CreateLlmKeyBody,
  LlmCatalogProvider,
  LlmCatalogResponse,
  LlmKeyRow,
  LlmKeyTestResponse,
  ProviderId,
  UpdateLlmKeyBody,
} from '../schemas';
import styles from './LlmKeyFormModal.module.css';

interface BaseProps {
  catalog: LlmCatalogResponse;
  onClose: () => void;
  onTest: (input: { provider: ProviderId; apiKey: string }) => Promise<LlmKeyTestResponse>;
  saving: boolean;
}

interface AddProps extends BaseProps {
  mode: 'add';
  initial?: undefined;
  onSave: (body: CreateLlmKeyBody) => void;
}

interface EditProps extends BaseProps {
  mode: 'edit';
  initial: LlmKeyRow;
  onSave: (body: UpdateLlmKeyBody) => void;
}

type Props = AddProps | EditProps;

function firstAvailable(providers: LlmCatalogProvider[]): LlmCatalogProvider {
  const p = providers.find((x) => x.available);
  if (!p) throw new Error('LLM catalog has no available providers');
  return p;
}

export function LlmKeyFormModal(props: Props) {
  const initialProvider: LlmCatalogProvider =
    props.mode === 'edit'
      ? (props.catalog.providers.find((p) => p.id === props.initial.provider) ??
        firstAvailable(props.catalog.providers))
      : firstAvailable(props.catalog.providers);

  const [label, setLabel] = useState(props.mode === 'edit' ? props.initial.label : '');
  const [provider, setProvider] = useState<ProviderId>(initialProvider.id);
  const [model, setModel] = useState(
    props.mode === 'edit' ? props.initial.model : (initialProvider.models[0]?.id ?? ''),
  );
  const [apiKey, setApiKey] = useState('');
  const [makeDefault, setMakeDefault] = useState(
    props.mode === 'edit' ? props.initial.isDefault : false,
  );
  const [testResult, setTestResult] = useState<LlmKeyTestResponse | null>(null);
  const [testing, setTesting] = useState(false);

  const currentProvider = useMemo(
    () => props.catalog.providers.find((p) => p.id === provider) ?? initialProvider,
    [props.catalog.providers, provider, initialProvider],
  );

  function handleProviderChange(next: ProviderId) {
    const nextProvider = props.catalog.providers.find((p) => p.id === next);
    if (!nextProvider?.available) return;
    setProvider(next);
    setModel(nextProvider.models[0]?.id ?? '');
    setTestResult(null);
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await props.onTest({ provider, apiKey });
      setTestResult(r);
    } finally {
      setTesting(false);
    }
  }

  function handleSave() {
    if (props.mode === 'add') {
      props.onSave({ label, provider, model, apiKey, makeDefault });
    } else {
      const body: UpdateLlmKeyBody = { label, model, makeDefault };
      if (apiKey.trim() !== '') body.apiKey = apiKey;
      props.onSave(body);
    }
  }

  const apiKeyHint =
    props.mode === 'edit'
      ? t('Leave blank to keep existing key.')
      : t('Paste your provider API key.');

  const title = props.mode === 'add' ? t('Add LLM key') : t('Edit LLM key');

  const actions = (
    <>
      <Button variant="ghost" onClick={props.onClose} disabled={props.saving}>
        {t('Cancel')}
      </Button>
      <Button variant="primary" onClick={handleSave} isLoading={props.saving}>
        {t('Save')}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) props.onClose();
      }}
      title={title}
      size="md"
      actions={actions}
    >
      <TextField
        label={t('Label')}
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        maxLength={100}
        required
      />

      <fieldset className={`${styles.field} ${styles['provider-fieldset']}`}>
        <legend className={styles['field-label']}>{t('Provider')}</legend>
        {props.catalog.providers.map((p) => {
          const rowClass = [styles['radio-row'], !p.available ? styles['radio-row-disabled'] : '']
            .filter(Boolean)
            .join(' ');
          return (
            <label key={p.id} className={rowClass}>
              <input
                type="radio"
                name="provider"
                value={p.id}
                checked={provider === p.id}
                disabled={!p.available}
                onChange={() => handleProviderChange(p.id)}
              />
              <span>
                {p.label}
                {!p.available ? ` (${t('coming soon')})` : ''}
              </span>
            </label>
          );
        })}
      </fieldset>

      <label className={styles.field}>
        <span className={styles['field-label']}>{t('Model')}</span>
        <select className={styles.select} value={model} onChange={(e) => setModel(e.target.value)}>
          {currentProvider.models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </label>

      <TextField
        label={t('API key')}
        type="password"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        autoComplete="off"
        placeholder={props.mode === 'edit' ? `…${props.initial.fingerprint}` : ''}
        hint={apiKeyHint}
      />

      <label className={styles['checkbox-row']}>
        <input
          type="checkbox"
          checked={makeDefault}
          onChange={(e) => setMakeDefault(e.target.checked)}
        />
        <span>{t('Make this the default key')}</span>
      </label>

      <div className={styles['test-row']}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void handleTest();
          }}
          disabled={testing || apiKey.trim() === ''}
          isLoading={testing}
        >
          {t('Test connection')}
        </Button>
        {testResult?.ok === true && (
          <span className={styles.ok}>
            ✓ {t('Connected')} ({testResult.latencyMs} ms)
          </span>
        )}
        {testResult?.ok === false && (
          <span className={styles.err}>✗ {testResult.error.message}</span>
        )}
      </div>
    </Modal>
  );
}
